import React, { useState, useEffect, Suspense, lazy } from 'react';
import Sidebar from './components/Sidebar';
import CategoriasGrid from './components/CategoriasGrid';
import GruposGrid from './components/GruposGrid';
import InventarioLista from './components/InventarioLista';
import DetalleInventarioView from './components/DetalleInventarioView';
import StockBajoView from './components/StockBajoView';
import ProductoDetalleModal from './components/ProductoDetalleModal';
import FormularioNuevoProducto from './components/FormularioNuevoProducto';
import GestionCategorias from './components/GestionCategorias';
import CatalogoPublico from './components/CatalogoPublico';
import CompartirCatalogoModal from './components/CompartirCatalogoModal';
import ReportesView from './components/ReportesView';
import DashboardView from './components/DashboardView';
import MetasView from './components/MetasView';
import VentasHoyView from './components/VentasHoyView';
import MiCuentaView from './components/MiCuentaView';
import POSView from './components/POSView';
import ServiciosView from './components/ServiciosView';
import CotizacionesView from './components/CotizacionesView';
import AgendaView from './components/AgendaView';
import OrdenesView from './components/OrdenesView';
import GastosView from './components/GastosView';
import FacturasView from './components/FacturasView';
import ClientesView from './components/ClientesView';
import CuentasView from './components/CuentasView';
import ConfiguracionView from './components/ConfiguracionView';
import BalanceView from './components/BalanceView';
import CierreView from './components/CierreView';
import CorteDiaView from './components/CorteDiaView';
import PorCobrarView from './components/PorCobrarView';
import PedidosView from './components/PedidosView';
import PlanesView from './components/PlanesView';
import AdminSuscripcionesView from './components/AdminSuscripcionesView';
import AyudaView from './components/AyudaView';
import BotonComoFunciona from './components/BotonComoFunciona';
import AvisoMembresia from './components/AvisoMembresia';
import AvisoSinConexion from './components/AvisoSinConexion';
import VentasPendientes from './components/VentasPendientes';
import LoginView from './components/LoginView';
import NuevaClaveView from './components/NuevaClaveView';
import { EN_TIENDA } from './utils/tienda';
import LegalView from './components/LegalView';
import EquipoView from './components/EquipoView';
import { verInvitacion } from './services/equipoService';
import OnboardingModal from './components/OnboardingModal';
import CotizacionPublica from './components/CotizacionPublica';
import CatalogoServiciosPublico from './components/CatalogoServiciosPublico';
import ErrorBoundary from './components/ErrorBoundary';
import { sonarAviso } from './services/avisosPedidos';
import { ToastProvider, useToast } from './context/ToastContext';
import { CategoriasProvider, useCategorias } from './context/CategoriasContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useProductos } from './hooks/useProductos';
import { contarPedidosPendientes } from './services/pedidosService';
import { setNegocioId } from './services/negocioActual';
import { supabase } from './services/supabaseClient';
import { obtenerPanel, aplicarPanel } from './services/tema';
import { obtenerCategoria } from './utils/categorias';
import { dimensionesDeCategoria, coincideDimension, DIMENSIONES } from './utils/agrupar';
import { linkCatalogo, copiarTexto } from './utils/compartir';
import { NEGOCIO_CATALOGO_PREDETERMINADO } from './config';
import { getUmbralStock, setUmbralStock } from './utils/stockBajo';
import { capturarOrigenRegistro } from './utils/origenRegistro';
import { tieneMetaConectado } from './services/metaAdsService';

// Módulo de anuncios: se descarga solo al abrirlo (gráficas y tablas pesadas).
const AnunciosMetaView = lazy(() => import('./components/AnunciosMetaView'));
// Agente de contenido (piloto, sql/099): solo la dueña de un negocio con el módulo prendido.
const ContenidoView = lazy(() => import('./components/ContenidoView'));

function AppContent() {
  const { negocio, sesion, cerrarSesion, esAdmin, esVendedora, recargarSuscripcion, puede } = useAuth();
  const g = !esVendedora; // dueña: Mi plan, Mi equipo y Ajustes
  // El agente de contenido lo usa solo la dueña (la BD solo le deja escribir a ella).
  const esDuenaContenido = negocio?.rol === 'duena' && !!negocio?.contenido_activo;
  // Lo demás depende de los permisos que la dueña le dio a cada persona (Mi equipo → Permisos).
  const { mostrarToast } = useToast();
  const { categorias } = useCategorias();
  const { productos, cargando, error, recargar } = useProductos();
  const [vistaActual, setVistaActual] = useState('inicio');
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [dimensionActual, setDimensionActual] = useState('talla'); // 'talla' | 'marca'
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null); // valor o '__sin__'
  const [soloStockBajo, setSoloStockBajo] = useState(false);
  const [umbralStock, setUmbralStockState] = useState(getUmbralStock);
  const [busqueda, setBusqueda] = useState('');
  const [productoDetalle, setProductoDetalle] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarCategorias, setMostrarCategorias] = useState(false);
  const [mostrarCompartir, setMostrarCompartir] = useState(false);
  const [pedidosPendientes, setPedidosPendientes] = useState(0);
  const [ventaPedido, setVentaPedido] = useState(null); // pedido del catálogo que se está vendiendo
  const [tieneMeta, setTieneMeta] = useState(false); // la dueña conectó su cuenta de Meta Ads (sql/097)

  // Aplica el color de marca del negocio a toda la app (respaldo: rosa).
  useEffect(() => {
    document.documentElement.style.setProperty('--color-primario', negocio?.color_principal || '#DB2777');
  }, [negocio?.color_principal]);

  // Aplica el estilo del panel guardado por el negocio (morado / lavanda).
  useEffect(() => {
    if (negocio?.id) aplicarPanel(obtenerPanel());
  }, [negocio?.id]);

  // Regreso desde la página de pago de Tilopay (?pago=gracias): el aviso de
  // Tilopay activa la cuenta en segundos; recargamos la membresía un par de veces.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pago') !== 'gracias') return undefined;
    window.history.replaceState(null, '', window.location.pathname);
    setVistaActual('planes');
    mostrarToast('¡Gracias por tu pago! 💜 Estamos confirmándolo; tu plan se activa en unos segundos.', 'exito', 8000);
    const t1 = setTimeout(recargarSuscripcion, 5000);
    const t2 = setTimeout(recargarSuscripcion, 15000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vuelta de "Conectar mi Facebook e Instagram" (?meta=ok|elegir|error&motivo=…).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get('meta');
    if (!r) return;
    window.history.replaceState(null, '', window.location.pathname);
    // "Mis marcas" (sql/140): si se conectaron las redes de una marca, se abre esa marca en Contenido.
    // Si se empezó desde Contenido (también la marca principal), se vuelve ahí.
    let volver = null;
    try { volver = sessionStorage.getItem('kontrol-meta-volver'); sessionStorage.removeItem('kontrol-meta-volver'); } catch { /* sin almacenamiento */ }
    const uuid = (v) => /^[0-9a-f-]{36}$/i.test(v || '');
    const marca = uuid(params.get('marca')) ? params.get('marca') : uuid(volver) ? volver : null;
    if (marca || volver) {
      try { if (marca) sessionStorage.setItem('kontrol-meta-marca', marca); } catch { /* sin almacenamiento */ }
      setVistaActual('contenido');
    } else setVistaActual('config');
    const motivos = {
      cancelado: 'No se conectó: cancelaste el permiso en Facebook.',
      sin_paginas: 'No encontramos una página de Facebook que administres. Crea la página de tu negocio (o pide que te hagan administradora) y vuelve a conectar.',
      vencio: 'El enlace venció. Toca "Conectar" otra vez.',
      no_disponible: 'Conectar redes estará disponible muy pronto.',
    };
    if (r === 'ok') mostrarToast('¡Listo! Tu Facebook e Instagram quedaron conectados 🎉', 'exito', 7000);
    else if (r === 'elegir') mostrarToast('Casi listo: elige con qué página publicas.', 'exito', 7000);
    else mostrarToast(motivos[params.get('motivo')] || 'No se pudo conectar con Facebook. Intenta de nuevo.', 'error', 9000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atajos del ícono instalado (mantener presionado): /?abrir=pos|ventashoy|inventario
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const abrir = params.get('abrir');
    if (params.has('origen') || abrir) window.history.replaceState(null, '', window.location.pathname);
    if (abrir === 'pos' || abrir === 'ventashoy' || abrir === 'inventario' || abrir === 'pedidos') setVistaActual(abrir);
    if (abrir === 'metas') {
      try { if (params.get('pestana')) sessionStorage.setItem('kontrol_metas_pestana', params.get('pestana')); } catch { /* sin almacenamiento */ }
      setVistaActual('metas'); // la vista solo se muestra a la dueña
    }
    if (abrir === 'admin') setVistaActual('admin'); // solo se muestra a la admin de Kontrol
  }, []);

  // Cuenta de pedidos pendientes para el aviso del header.
  const refrescarPedidos = () => contarPedidosPendientes().then(setPedidosPendientes).catch(() => {});
  useEffect(() => { refrescarPedidos(); }, []);

  // Aviso de pedido nuevo (push, sql/119): con Kontrol abierto suena, avisa y
  // actualiza el contador; al tocar la notificación abre Pedidos.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const alMensaje = (ev) => {
      const d = ev.data || {};
      if (d.tipo === 'nuevo-pedido') { sonarAviso(); refrescarPedidos(); mostrarToast(`${d.titulo || '🛍️ Nuevo pedido'}${d.cuerpo ? ` · ${d.cuerpo}` : ''}`, 'exito', 9000); }
      if (d.tipo === 'abrir-pedidos') { setVistaActual('pedidos'); refrescarPedidos(); }
      // Avisos de Mis metas (recordatorios y resumen de la mañana): no son pedidos.
      if (d.tipo === 'aviso-meta') mostrarToast(`${d.titulo || '⏰ Recordatorio'}${d.cuerpo ? ` · ${d.cuerpo}` : ''}`, 'info', 9000);
      if (d.tipo === 'abrir-vista' && (d.vista === 'metas' || d.vista === 'admin')) setVistaActual(d.vista);
    };
    navigator.serviceWorker.addEventListener('message', alMensaje);
    return () => navigator.serviceWorker.removeEventListener('message', alMensaje);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Anuncios de Meta" aparece solo para la dueña de un negocio con Meta conectado.
  useEffect(() => {
    if (!g || !negocio?.id) { setTieneMeta(false); return; }
    tieneMetaConectado().then(setTieneMeta).catch(() => setTieneMeta(false));
  }, [g, negocio?.id]);

  const irInicio = () => {
    setVistaActual('inicio');
    setCategoriaSeleccionada(null);
    setGrupoSeleccionado(null);
    setSoloStockBajo(false);
    setBusqueda('');
  };

  const irInventario = ({ categoria = null, stockBajo = false, dimension = 'talla', grupo = null } = {}) => {
    setVistaActual('inventario');
    setCategoriaSeleccionada(categoria);
    setDimensionActual(dimension);
    setGrupoSeleccionado(grupo);
    setSoloStockBajo(stockBajo);
    setBusqueda('');
  };

  // Al abrir una categoría: si usa talla o marca, mostrar las tarjetas de grupo;
  // si no, ir directo a la lista de productos.
  const abrirCategoria = (categoria) => {
    const dims = dimensionesDeCategoria(productos, categoria);
    if (dims.length > 0) {
      setVistaActual('grupos');
      setCategoriaSeleccionada(categoria);
      setDimensionActual(dims[0]);
      setGrupoSeleccionado(null);
      setBusqueda('');
    } else {
      irInventario({ categoria });
    }
  };

  // El umbral se guarda por negocio: se relee cuando cambia el negocio.
  useEffect(() => { setUmbralStockState(getUmbralStock()); }, [negocio?.id]);
  const cambiarUmbralStock = (n) => { setUmbralStock(n); setUmbralStockState(n); };
  const irStockBajo = () => { setVistaActual('stockbajo'); setBusqueda(''); };

  const productosConStockBajo = productos.filter((p) => Number(p.stock || 0) <= umbralStock);

  const texto = busqueda.trim().toLowerCase();
  const productosFiltrados = productos
    .filter((p) => !categoriaSeleccionada || p.categoria === categoriaSeleccionada)
    .filter((p) => coincideDimension(p, dimensionActual, grupoSeleccionado))
    .filter((p) => !soloStockBajo || Number(p.stock || 0) <= umbralStock)
    .filter((p) =>
      !texto ||
      (p.nombre || '').toLowerCase().includes(texto) ||
      (p.marca || '').toLowerCase().includes(texto) ||
      (p.codigo || '').toLowerCase().includes(texto)
    );

  // Volver desde la lista: si la categoría tiene grupos, regresa a las tarjetas.
  const volverDeInventario = () => {
    if (categoriaSeleccionada && dimensionesDeCategoria(productos, categoriaSeleccionada).length > 0) {
      setVistaActual('grupos');
      setGrupoSeleccionado(null);
      setBusqueda('');
    } else {
      irInicio();
    }
  };

  // Compartir por WhatsApp los productos del grupo actual (talla o marca).
  const compartirGrupoActual = async () => {
    const cat = categoriaSeleccionada ? obtenerCategoria(categorias, categoriaSeleccionada) : null;
    const valor = grupoSeleccionado && grupoSeleccionado !== '__sin__' ? grupoSeleccionado : null;
    const link = linkCatalogo({
      negocioId: negocio?.id,
      slug: negocio?.slug,
      cat: cat?.prefijo,
      tipo: dimensionActual === 'subtipo' ? valor : null,
      talla: dimensionActual === 'talla' ? valor : null,
      marca: dimensionActual === 'marca' ? valor : null,
      origen: 'whatsapp',
    });
    const etiqueta = valor
      ? `${DIMENSIONES[dimensionActual].etiqueta.toLowerCase()} ${valor}`
      : categoriaSeleccionada;
    const mensaje = `¡Mira nuestros productos en ${etiqueta}! 🛍️💕\n${link}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener,noreferrer');
    const copiado = await copiarTexto(link);
    if (copiado) mostrarToast('Link copiado y abriendo WhatsApp ✅', 'exito');
  };

  return (
    <div className="k-app">
      <Sidebar
        negocio={negocio}
        vistaActual={vistaActual}
        totalProductos={productos.length}
        stockBajo={productosConStockBajo.length}
        pedidosPendientes={pedidosPendientes}
        onIrInicio={irInicio}
        onVender={() => { setVentaPedido(null); setVistaActual('pos'); setBusqueda(''); }}
        onNuevoProducto={() => setMostrarFormulario(true)}
        onIrInventario={() => irInventario()}
        onVerServicios={() => { setVistaActual('servicios'); setBusqueda(''); }}
        onVerCotizaciones={() => { setVistaActual('cotizaciones'); setBusqueda(''); }}
        onVerAgenda={() => { setVistaActual('agenda'); setBusqueda(''); }}
        onVerOrdenes={() => { setVistaActual('ordenes'); setBusqueda(''); }}
        onVerPedidos={() => { setVistaActual('pedidos'); setBusqueda(''); }}
        onCompartir={() => setMostrarCompartir(true)}
        onVerBalance={() => { setVistaActual('balance'); setBusqueda(''); }}
        onVerCuentas={() => { setVistaActual('cuentas'); setBusqueda(''); }}
        onVerGastos={() => { setVistaActual('gastos'); setBusqueda(''); }}
        onVerFacturas={() => { setVistaActual('facturas'); setBusqueda(''); }}
        onVerPorCobrar={() => { setVistaActual('porcobrar'); setBusqueda(''); }}
        onVerCierre={() => { setVistaActual('cierre'); setBusqueda(''); }}
        onVerCorte={() => { setVistaActual('corte'); setBusqueda(''); }}
        onVerReportes={() => { setVistaActual('reportes'); setBusqueda(''); }}
        onVerClientes={() => { setVistaActual('clientes'); setBusqueda(''); }}
        onVerConfiguracion={() => { setVistaActual('config'); setBusqueda(''); }}
        onVerPlanes={() => { setVistaActual('planes'); setBusqueda(''); }}
        onVerAdmin={esAdmin ? () => { setVistaActual('admin'); setBusqueda(''); } : null}
        onVerEquipo={() => { setVistaActual('equipo'); setBusqueda(''); }}
        onVerAyuda={() => { setVistaActual('ayuda'); setBusqueda(''); }}
        esVendedora={esVendedora}
        puede={puede}
        onVerVentasHoy={() => { setVistaActual('ventashoy'); setBusqueda(''); }}
        onVerMetas={() => { setVistaActual('metas'); setBusqueda(''); }}
        onVerCuenta={() => { setVistaActual('cuenta'); setBusqueda(''); }}
        onVerAnuncios={tieneMeta ? () => { setVistaActual('anuncios'); setBusqueda(''); } : null}
        onVerContenido={esDuenaContenido ? () => { setVistaActual('contenido'); setBusqueda(''); } : null}
        onVerStockBajo={irStockBajo}
        onCerrarSesion={cerrarSesion}
      />

      <main className="k-main">
      <AvisoSinConexion />
      <VentasPendientes onSubidas={recargar} />
      {g && <AvisoMembresia onVerPlanes={() => { setVistaActual('planes'); setBusqueda(''); }} onVerCuenta={() => setVistaActual('cuenta')} />}
      <BotonComoFunciona vista={vistaActual} />

      {vistaActual === 'ayuda' && <AyudaView onVolver={irInicio} />}

      {vistaActual === 'ventashoy' && <VentasHoyView onVolver={irInicio} />}
      {vistaActual === 'metas' && !esVendedora && (
        <MetasView onVolver={irInicio} onIr={(v) => { if (v === 'inventario') irInventario(); else { setVistaActual(v); setBusqueda(''); } }} />
      )}
      {vistaActual === 'cuenta' && <MiCuentaView onVolver={irInicio} />}

      {g && tieneMeta && vistaActual === 'anuncios' && (
        <Suspense fallback={<div className="k-page"><p style={{ color: 'var(--k-muted)' }}>Cargando tus anuncios…</p></div>}>
          <AnunciosMetaView onVolver={irInicio} />
        </Suspense>
      )}

      {esDuenaContenido && vistaActual === 'contenido' && (
        <Suspense fallback={<div className="k-page"><p style={{ color: 'var(--k-muted)' }}>Cargando tu contenido…</p></div>}>
          <ContenidoView onVolver={irInicio} />
        </Suspense>
      )}

      {puede('reportes') && vistaActual === 'reportes' && (
        <ReportesView productos={productos} onVolver={irInicio} onVerPlanes={() => setVistaActual('planes')} />
      )}

      {vistaActual === 'pos' && (
        <POSView key={ventaPedido?.id || 'pos'} productos={productos} onVendido={recargar} onProductoCreado={recargar}
          pedido={ventaPedido}
          onPedidoVendido={() => { setVentaPedido(null); refrescarPedidos(); }}
          onVolver={() => { if (ventaPedido) { setVentaPedido(null); setVistaActual('pedidos'); } else irInicio(); }} />
      )}

      {vistaActual === 'servicios' && <ServiciosView onVolver={irInicio} />}

      {vistaActual === 'cotizaciones' && <CotizacionesView onVolver={irInicio} />}
      {vistaActual === 'agenda' && <AgendaView onVolver={irInicio} />}
      {vistaActual === 'ordenes' && <OrdenesView onVolver={irInicio} />}

      {puede('gastos') && vistaActual === 'gastos' && <GastosView onVolver={irInicio} />}
      {g && vistaActual === 'facturas' && <FacturasView onVolver={irInicio} onIrConfiguracion={() => setVistaActual('config')} />}

      {vistaActual === 'clientes' && <ClientesView onVolver={irInicio} />}

      {puede('cuentas') && vistaActual === 'cuentas' && <CuentasView onVolver={irInicio} />}

      {g && vistaActual === 'config' && <ConfiguracionView onVolver={irInicio} />}

      {g && vistaActual === 'planes' && <PlanesView onVolver={irInicio} />}
      {g && vistaActual === 'equipo' && <EquipoView onVolver={irInicio} onVerPlanes={() => setVistaActual('planes')} />}
      {vistaActual === 'admin' && esAdmin && <AdminSuscripcionesView onVolver={irInicio} />}

      {puede('balance') && vistaActual === 'balance' && <BalanceView onVolver={irInicio} onCambio={recargar} productos={productos} />}

      {puede('cierre') && vistaActual === 'cierre' && <CierreView onVolver={irInicio} onCambio={recargar} />}

      {puede('corte') && vistaActual === 'corte' && <CorteDiaView onVolver={irInicio} onCambio={recargar} />}

      {puede('por_cobrar') && vistaActual === 'porcobrar' && <PorCobrarView onVolver={irInicio} onCambio={recargar} productos={productos} />}

      {vistaActual === 'pedidos' && <PedidosView onVolver={irInicio} onCambio={refrescarPedidos}
        onRegistrarVenta={(p) => { setVentaPedido(p); setVistaActual('pos'); setBusqueda(''); }} />}

      {vistaActual === 'inicio' && (
        <DashboardView
          negocio={negocio}
          verCostos={puede('costos')}
          verCuentas={puede('cuentas')}
          verGastos={puede('gastos')}
          soloDe={puede('ventas_todas') ? null : sesion?.user?.id}
          onIrVentasHoy={() => setVistaActual('ventashoy')}
          onIrMetas={esVendedora ? null : () => { setVistaActual('metas'); setBusqueda(''); }}
          onIr={(vista) => { setVistaActual(vista); setBusqueda(''); }}
          onIrStockBajo={puede('productos') ? irStockBajo : null}
          onIrCuentas={() => setVistaActual('cuentas')}
          onIrGastos={() => setVistaActual('gastos')}
        />
      )}

      {vistaActual === 'categorias' && (
        <CategoriasGrid
          onSeleccionar={abrirCategoria}
          onAdministrar={() => setMostrarCategorias(true)}
        />
      )}

      {vistaActual === 'grupos' && (
        <GruposGrid
          categoria={categoriaSeleccionada}
          productos={productos}
          dimension={dimensionActual}
          dimensiones={dimensionesDeCategoria(productos, categoriaSeleccionada)}
          onCambiarDimension={setDimensionActual}
          onSeleccionar={(grupo) => irInventario({ categoria: categoriaSeleccionada, dimension: dimensionActual, grupo })}
          onTodas={() => irInventario({ categoria: categoriaSeleccionada, dimension: dimensionActual })}
          onVolver={() => setVistaActual('categorias')}
          onVerCategorias={() => setVistaActual('categorias')}
        />
      )}

      {vistaActual === 'inventario' && (
        <InventarioLista
          productos={productosFiltrados}
          categoriaSeleccionada={categoriaSeleccionada}
          dimension={dimensionActual}
          grupoSeleccionado={grupoSeleccionado}
          soloStockBajo={soloStockBajo}
          busqueda={busqueda}
          onBuscar={setBusqueda}
          cargando={cargando}
          error={error}
          onVolver={volverDeInventario}
          onVerProducto={setProductoDetalle}
          onReintentar={recargar}
          onVerCategorias={() => setVistaActual('categorias')}
          onVerDetalle={puede('costos') ? () => { setVistaActual('detalle'); setBusqueda(''); } : null}
          stockBajo={productosConStockBajo.length}
          onVerStockBajo={puede('productos') ? irStockBajo : null}
          onCompartirGrupo={grupoSeleccionado && grupoSeleccionado !== '__sin__' ? compartirGrupoActual : null}
        />
      )}

      {puede('productos') && vistaActual === 'stockbajo' && (
        <StockBajoView
          productos={productos}
          umbral={umbralStock}
          onCambiarUmbral={cambiarUmbralStock}
          onVolver={() => irInventario()}
          onVerProducto={setProductoDetalle}
          onCambio={recargar}
        />
      )}

      {puede('costos') && vistaActual === 'detalle' && (
        <DetalleInventarioView productos={productos} onVolver={() => irInventario()} onCambio={recargar} />
      )}

      </main>

      {productoDetalle && (
        <ProductoDetalleModal
          key={productoDetalle.id}
          producto={productoDetalle}
          onCerrar={() => setProductoDetalle(null)}
          onActualizado={recargar}
        />
      )}

      {puede('productos') && mostrarFormulario && (
        <FormularioNuevoProducto
          onCerrar={() => setMostrarFormulario(false)}
          onGuardado={recargar}
        />
      )}

      {mostrarCategorias && (
        <GestionCategorias onCerrar={() => setMostrarCategorias(false)} />
      )}

      {mostrarCompartir && (
        <CompartirCatalogoModal
          negocioId={negocio?.id}
          slug={negocio?.slug}
          categorias={categorias}
          productos={productos}
          onCerrar={() => setMostrarCompartir(false)}
        />
      )}
    </div>
  );
}

// Sin sesión: abre directo el acceso. La página de venta vive ahora en la web
// (Cloudflare Pages, carpeta sitio/): https://automatikaia.com/kontrol/
// /?registro abre "Crear cuenta" (enlaces de la web, anuncios y WhatsApp).
// "← Conocer Kontrol" lleva a la web (en la app de tienda no se muestra).
export const WEB_KONTROL = 'https://automatikaia.com/kontrol/';
function Acceso() {
  const [modo] = useState(() => (new URLSearchParams(window.location.search).has('registro') ? 'registro' : 'login'));
  // De dónde llegó (?o=instagram, web-hero…) para guardarlo al crear la cuenta (sql/141).
  useEffect(() => { window.scrollTo(0, 0); document.title = 'Kontrol'; capturarOrigenRegistro(); }, []);
  return <LoginView key={modo} modoInicial={modo} onVolver={EN_TIENDA ? null : () => { window.location.href = WEB_KONTROL; }} />;
}

// Muestra login, pantalla de carga o la app según la sesión.
function AppConSesion() {
  const { sesion, negocio, cargando, actualizarNegocioLocal, recuperando } = useAuth();

  if (cargando) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FDF2F8', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ textAlign: 'center', color: 'var(--color-primario,#DB2777)' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🤖</div>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Cargando tu negocio...</p>
        </div>
      </div>
    );
  }

  if (!sesion) return <Acceso />;
  if (recuperando) return <NuevaClaveView />; // abrió el enlace de "¿Olvidaste tu contraseña?"

  if (!negocio) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FDF2F8', fontFamily: 'Arial, sans-serif', padding: '16px' }}>
        <p style={{ color: '#991B1B', fontWeight: 600, textAlign: 'center' }}>
          ⚠️ No encontramos un negocio asociado a tu cuenta.<br />
          Cierra sesión e intenta de nuevo, o contacta a soporte de Automatika.ia.
        </p>
      </div>
    );
  }

  // Onboarding: aparece una sola vez, apenas se crea la cuenta. Al terminar u
  // omitir, se marca en la BD; acá reflejamos el cambio local para entrar ya.
  // (Solo la dueña: una persona invitada entra directo.)
  if (!negocio.onboarding_completado && negocio.rol === 'duena') {
    return (
      <OnboardingModal
        nombreNegocio={negocio.nombre}
        onListo={(rubro, vende) => actualizarNegocioLocal({ onboarding_completado: new Date().toISOString(), ...(rubro ? { rubro } : {}), ...(vende ? { vende } : {}) })}
      />
    );
  }

  return (
    <CategoriasProvider>
      <AppContent />
    </CategoriasProvider>
  );
}

// Enlace de invitación: valida el token y muestra el registro con el correo
// fijo. Si ya hay una sesión abierta, pide cerrarla primero.
function UnirseView({ token }) {
  const { sesion, cargando, cerrarSesion } = useAuth();
  const [inv, setInv] = useState(undefined); // undefined = cargando, null = no válida
  useEffect(() => { verInvitacion(token).then(setInv); }, [token]);

  const caja = (contenido) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FDF2F8', fontFamily: 'Arial, sans-serif', padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '380px', textAlign: 'center', color: '#374151' }}>{contenido}</div>
    </div>
  );
  if (cargando || inv === undefined) return caja(<p style={{ margin: 0 }}>Cargando invitación…</p>);
  if (sesion) {
    return caja(<>
      <p style={{ marginTop: 0 }}>Ya tienes una sesión abierta en este navegador. Para aceptar la invitación, cierra sesión primero.</p>
      <button onClick={cerrarSesion} style={{ padding: '10px 16px', border: 'none', borderRadius: '10px', background: '#6D28D9', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cerrar sesión</button>
      <p style={{ marginBottom: 0 }}><a href="/" style={{ color: '#6D28D9', fontSize: '13px' }}>Ir a Kontrol</a></p>
    </>);
  }
  if (!inv) {
    return caja(<>
      <div style={{ fontSize: '36px' }}>🔗</div>
      <p style={{ fontWeight: 700, margin: '8px 0' }}>Esta invitación ya no es válida</p>
      <p style={{ fontSize: '14px', margin: 0 }}>Puede que haya vencido, que ya se haya usado o que la cancelaran. Pídele a la dueña del negocio un enlace nuevo.</p>
    </>);
  }
  return <LoginView invitacion={{ token, ...inv }} />;
}

// Catálogo por "nombre corto" (ej. kontrol.automatikaia.com/lolitas).
// Busca el negocio con ese slug (solo catálogos públicos) y muestra su catálogo.
function CatalogoPorSlug({ slug }) {
  const [estado, setEstado] = useState('cargando'); // 'cargando' | 'ok' | 'noencontrado'
  useEffect(() => {
    let activo = true;
    supabase
      .from('negocios_publicos')
      .select('id')
      .eq('slug', slug)
      .eq('catalogo_publico', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!activo) return;
        if (data?.id) { setNegocioId(data.id); setEstado('ok'); }
        else setEstado('noencontrado');
      })
      .catch(() => { if (activo) setEstado('noencontrado'); });
    return () => { activo = false; };
  }, [slug]);

  const centro = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ECE5FA', padding: '16px', fontFamily: 'DM Sans, Arial, sans-serif' };
  if (estado === 'cargando') {
    return <div style={centro}><p style={{ color: '#6D28D9', fontWeight: 700 }}>Cargando catálogo…</p></div>;
  }
  if (estado === 'noencontrado') {
    return (
      <div style={centro}>
        <div style={{ textAlign: 'center', color: '#4B5563', maxWidth: '360px' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔍</div>
          <p style={{ margin: 0, fontWeight: 700, color: '#1C1830' }}>No encontramos ese catálogo</p>
          <p style={{ margin: '6px 0 0', fontSize: '14px' }}>Revisa que el enlace esté bien escrito, o pídele a la tienda que te lo comparta de nuevo.</p>
        </div>
      </div>
    );
  }
  return (
    <CategoriasProvider>
      <CatalogoPublico slug={slug} />
    </CategoriasProvider>
  );
}

export default function App() {
  const ruta = window.location.pathname;

  // /unirse/<token> → registro de una persona invitada desde "Mi equipo".
  if (ruta.startsWith('/unirse/')) {
    const token = decodeURIComponent(ruta.replace(/^\/unirse\//, '').replace(/\/+$/, ''));
    return (
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <UnirseView token={token} />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // /terminos y /privacidad → documentos legales públicos.
  const legal = ruta.replace(/\/+$/, '').toLowerCase();
  if (['/terminos', '/privacidad', '/reembolsos', '/eliminar-cuenta'].includes(legal)) {
    return <LegalView doc={legal.slice(1)} />;
  }

  // /cotizacion/<token> → página pública de una cotización (el cliente la abre
  // sin cuenta con el enlace secreto). Va antes que el catálogo por slug.
  if (ruta.startsWith('/cotizacion/')) {
    const token = decodeURIComponent(ruta.replace(/^\/cotizacion\//, '').replace(/\/+$/, ''));
    return (
      <ErrorBoundary>
        <ToastProvider>
          <CotizacionPublica token={token} />
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // /servicios/<slug> → catálogo público de servicios (el cliente lo abre sin
  // cuenta). Va antes del catálogo por slug de una sola palabra.
  if (ruta.startsWith('/servicios/')) {
    const slug = decodeURIComponent(ruta.replace(/^\/servicios\//, '').replace(/\/+$/, '')).toLowerCase();
    return (
      <ErrorBoundary>
        <ToastProvider>
          <CatalogoServiciosPublico slug={slug} />
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // /catalogo?n=<id> → vista pública (compatibilidad con links viejos).
  if (ruta.startsWith('/catalogo')) {
    const params = new URLSearchParams(window.location.search);
    setNegocioId(params.get('n') || NEGOCIO_CATALOGO_PREDETERMINADO);
    return (
      <ErrorBoundary>
        <ToastProvider>
          <CategoriasProvider>
            <CatalogoPublico />
          </CategoriasProvider>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // /<nombre-corto> → catálogo por slug (ej. /lolitas).
  const seg = decodeURIComponent(ruta.replace(/^\/+|\/+$/g, ''));
  if (seg && !seg.includes('/') && !seg.includes('.') && /^[a-z0-9_-]+$/i.test(seg)) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <CatalogoPorSlug slug={seg.toLowerCase()} />
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <AppConSesion />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
